import { useDeleteGroupJobStatus } from "@/hooks/use-data";
import type { OpsJobKind, OpsJobStatus } from "@/lib/types";
import { useEffect } from "react";

export interface JobEntry {
  groupId: string;
  groupName: string;
  /** Which DB the items-sweep is hitting. Drives the env chip color and
   *  lets the operator distinguish prod vs dev rows when a single delete
   *  fans out into two banner entries. */
  env: "prod" | "dev";
  /** OpsJob discriminator. Controls the row label.
   *  - `group_delete_items_sweep` (default for backwards-compat with the
   *    IR-661 Phase 5 single-delete + Phase 8 bulk-delete paths)
   *  - `propagate_member_inheritance` (IR-661 Phase 7 Task 14 / Q4=4a:
   *    async member-propagation triggered by the group-edit "Apply
   *    changes to existing members" checkbox when ≥500 members live in
   *    either env). */
  kind?: OpsJobKind;
}

interface BannerProps {
  entries: Map<string, JobEntry>;
  onRemove: (jobId: string) => void;
}

const TERMINAL = new Set(["completed", "failed", "cancelled"]);

function rowStyle(borderColor: string): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderLeft: `3px solid ${borderColor}`,
    fontSize: 12,
    background: "var(--iris-bg-2)",
  };
}

function bannerTitle(entries: Map<string, JobEntry>): string {
  // Show a kind-aware title so operators know whether they're watching
  // delete sweeps, member propagations, or both.
  let hasDelete = false;
  let hasPropagate = false;
  for (const e of entries.values()) {
    const k = e.kind ?? "group_delete_items_sweep";
    if (k === "group_delete_items_sweep") hasDelete = true;
    if (k === "propagate_member_inheritance") hasPropagate = true;
  }
  if (hasDelete && hasPropagate) return "Active jobs";
  if (hasPropagate) return "Active member-propagation jobs";
  return "Active delete jobs";
}

function jobRowText(
  kind: OpsJobKind,
  data: OpsJobStatus,
  isComplete: boolean,
  isFailed: boolean,
): string {
  if (kind === "propagate_member_inheritance") {
    if (isComplete) return "Propagated to members";
    if (isFailed) return data.error || "Propagate failed";
    // Reuse the swept_items/total_items channel for member counts so the
    // backend doesn't need a parallel "members_propagated" field.
    return `Propagating ${data.swept_items.toLocaleString()}/${data.total_items.toLocaleString()} members (${data.progress_pct}%)`;
  }
  // Default: group_delete_items_sweep
  if (isComplete) return "Items swept";
  if (isFailed) return data.error || "Sweep failed";
  return `Sweeping ${data.swept_items.toLocaleString()}/${data.total_items.toLocaleString()} items (${data.progress_pct}%)`;
}

function EnvChip({ env }: { env: "prod" | "dev" }) {
  const tone = env === "prod" ? "var(--iris-neon-cyan)" : "var(--iris-neon-orange)";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "1px 6px",
        borderRadius: 3,
        border: `1px solid ${tone}`,
        color: tone,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: 0.5,
        textTransform: "uppercase",
      }}
    >
      {env}
    </span>
  );
}

function JobRow({
  jobId,
  entry,
  onRemove,
}: {
  jobId: string;
  entry: JobEntry;
  onRemove: (jobId: string) => void;
}) {
  const { data, error } = useDeleteGroupJobStatus(entry.groupId, jobId);

  useEffect(() => {
    if (!data) return;
    if (!TERMINAL.has(data.status)) return;
    const t = setTimeout(() => onRemove(jobId), 5000);
    return () => clearTimeout(t);
  }, [data, jobId, onRemove]);

  if (error) {
    return (
      <div style={rowStyle("var(--iris-neon-pink)")}>
        <EnvChip env={entry.env} />
        <span style={{ flex: 1 }}>{entry.groupName}</span>
        <span style={{ color: "var(--iris-neon-pink)" }}>Lost track of job</span>
        <button
          type="button"
          className="iris-btn"
          onClick={() => onRemove(jobId)}
          style={{ marginLeft: 8 }}
        >
          Dismiss
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={rowStyle("var(--iris-line-2)")}>
        <EnvChip env={entry.env} />
        <span style={{ flex: 1 }}>{entry.groupName}</span>
        <span style={{ color: "var(--iris-fg-4)" }}>Starting…</span>
      </div>
    );
  }

  const isFailed = data.status === "failed";
  const isComplete = data.status === "completed";
  const tone = isFailed
    ? "var(--iris-neon-pink)"
    : isComplete
      ? "var(--iris-neon-green)"
      : "var(--iris-neon-cyan)";

  // Resolve the live `kind` from the OpsJob row, falling back to the
  // entry-side hint for the spinner phase before the first poll lands.
  const kind: OpsJobKind = (data.kind as OpsJobKind) || entry.kind || "group_delete_items_sweep";

  return (
    <div style={rowStyle(tone)}>
      <EnvChip env={entry.env} />
      <span style={{ flex: 1, color: "var(--iris-fg-1)", fontWeight: 500 }}>{entry.groupName}</span>
      <span style={{ color: "var(--iris-fg-3)", marginRight: 12 }}>
        {jobRowText(kind, data, isComplete, isFailed)}
      </span>
      <div
        style={{
          width: 120,
          height: 6,
          background: "var(--iris-bg-2)",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${Math.max(0, Math.min(100, data.progress_pct))}%`,
            height: "100%",
            background: tone,
            transition: "width 200ms",
          }}
        />
      </div>
    </div>
  );
}

export function ActiveDeleteJobsBanner({ entries, onRemove }: BannerProps) {
  if (entries.size === 0) return null;
  return (
    <div
      className="iris-card"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: 0,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "6px 12px",
          fontSize: 11,
          color: "var(--iris-fg-3)",
          borderBottom: "1px solid var(--iris-line-1)",
        }}
      >
        {bannerTitle(entries)}
      </div>
      {[...entries.entries()].map(([jobId, entry]) => (
        <JobRow key={jobId} jobId={jobId} entry={entry} onRemove={onRemove} />
      ))}
    </div>
  );
}
